import { useRef, useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  TextField,
  MenuItem,
  Autocomplete,
  Typography,
  Box,
  Divider,
  Paper,
  Checkbox,
  FormControlLabel,
  Select,
  InputAdornment,
} from "@mui/material";
import {
  UserPlus,
  X,
  Plus,
  Trash2,
  Building2,
  Loader2,
  CheckCircle2,
  Search,
} from "lucide-react";
import { CircleFlag } from "react-circle-flags";
import { toast } from "react-toastify";
import { useImportContacts } from "@/api/contact/useImportContact";
import { useGetProspects } from "@/api/prospects/useGetProspects";
import { QueryContact, QueryPortfolio, QueryTypes } from "@/api/queries/types";
import { useGetSavedQuery2 } from "@/api/queries/useGetSavedQuery";
import { Button } from "@/components/Button/Button";
import { useKeycloak } from "@/providers/KeycloakProvider";
import { findPoststed } from "@/utils/findPostnummer";

const PHONE_COUNTRY_CODES = [
  { code: "+47", iso: "no" },
  { code: "+46", iso: "se" },
  { code: "+45", iso: "dk" },
  { code: "+358", iso: "fi" },
  { code: "+354", iso: "is" },
  { code: "+44", iso: "gb" },
  { code: "+1", iso: "us" },
  { code: "+49", iso: "de" },
  { code: "+33", iso: "fr" },
  { code: "+34", iso: "es" },
];

const COUNTRIES = [
  { value: "NO", label: "Norway" },
  { value: "SE", label: "Sweden" },
  { value: "DK", label: "Denmark" },
  { value: "FI", label: "Finland" },
  { value: "IS", label: "Iceland" },
  { value: "GB", label: "United Kingdom" },
  { value: "US", label: "United States" },
];

const LANGUAGES = [
  { value: "NO", label: "Norwegian" },
  { value: "EN", label: "English" },
  { value: "SE", label: "Swedish" },
  { value: "FI", label: "Finnish" },
];

interface NewContactProps {
  asButton?: boolean;
  title?: string;
}
export const NewContact = (props: NewContactProps) => {
  const { asButton, title } = props;

  const [isOpen, setIsOpen] = useState(false);

  if (asButton) {
    return (
      <>
        <Button
          icon={<UserPlus className="size-3" />}
          variant="outline"
          size="sm"
          onClick={() => setIsOpen(true)}
        >
          {title || "New contact"}
        </Button>
        <Dialog
          open={isOpen}
          onClose={() => setIsOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle className="flex items-center justify-between pr-12">
            {title || "New contact"}
            <Button
              variant="icon"
              size="sm"
              onClick={() => setIsOpen(false)}
              className="absolute top-3 right-3"
            >
              <X className="size-4" />
            </Button>
          </DialogTitle>
          <DialogContent>
            <NewContactContent onClose={() => setIsOpen(false)} />
          </DialogContent>
        </Dialog>
      </>
    );
  }
  return <NewContactContent />;
};

interface NewContactContentProps {
  title?: string;
  onClose?: () => void;
}

interface ContactAdvisor {
  id: string;
  name: string;
  share: number;
}

interface BrregAddress {
  adresse?: string[];
  postnummer?: string;
  poststed?: string;
}

interface BrregCompany {
  navn: string;
  organisasjonsnummer: string;
  mobil?: string;
  forretningsadresse?: BrregAddress;
  postadresse?: BrregAddress;
}

const NewContactContent = (props: NewContactContentProps) => {
  const { title, onClose } = props;

  const {
    data: availableAdvisors,
    loading: loadingAdvisors,
    refetch,
  } = useGetSavedQuery2<QueryPortfolio>(
    "advPortalPortfoliosWithParameters",
    {
      status: ["A"],
      portfolioType: ["Commisssion", "Commission"],
    },
    undefined,
    undefined,
    true
  );

  const { data: contactData, refetch: refetchContacts } =
    useGetSavedQuery2<QueryContact>(
      "advPortalContactsSimple",
      {
        limit: 4000,
        contactName: "%",
      },
      false,
      "network-only",
      true
    );

  const { data: prospectData } = useGetProspects();

  // Normalizes phone to bare digits, stripping +47 / 0047 prefix
  const normalizePhone = (value: string): string => {
    const digits = value.replace(/\D/g, "");
    if (digits.length === 12 && digits.startsWith("0047"))
      return digits.slice(4);
    if (digits.length === 10 && digits.startsWith("47")) return digits.slice(2);
    return digits;
  };

  const { data: types, loading: typesLoading } = useGetSavedQuery2<QueryTypes>(
    "advPortalTypes",
    {}
  );

  const advisorSplits =
    types.filter((x) => x.type === "TagRepresentative").map((x) => x.name) ||
    [];

  const doesContactExist = (fnr: string): boolean => {
    if (!fnr || !contactData) return false;
    // Check for exact match with the F prefix format
    const contactId = `F${fnr}`;
    return contactData.some((contact) => contact.contactId === contactId);
  };

  const { data: countries, loading: countriesLoading } =
    useGetSavedQuery2<QueryTypes>("advPortalTypes", {});

  const { linkedContact } = useKeycloak();

  const SUBTYPES = [
    { value: "Company", label: "Company" },
    { value: "Person", label: "Person" },
  ];

  const [type, setType] = useState<string>("");
  const [classification, setClassification] = useState<string>("");
  const [taxCountry, setTaxCountry] = useState<string>("NO");
  const [language, setLanguage] = useState<string>("NO");
  const [nationality, setNationality] = useState<string>("NO");
  const [advisorList, setAdvisorList] = useState<ContactAdvisor[]>([]);
  const [useCustomShares, setUseCustomShares] = useState<boolean>(false);

  // Error states
  const [errors, setErrors] = useState({
    type: "",
    name: "",
    fnr: "",
    address1: "",
    zip: "",
    city: "",
    email: "",
    phone: "",
    classification: "",
    advisorList: "",
  });

  const meAsAdvisor = useMemo(() => {
    return availableAdvisors?.find(
      (x) => x.contact_id === Number(linkedContact)
    );
  }, [availableAdvisors, linkedContact]);

  useEffect(() => {
    if (meAsAdvisor) {
      setAdvisorList([
        {
          id: meAsAdvisor.contactId,
          name: meAsAdvisor.contactName,
          share: 100,
        },
      ]);
    }
  }, [meAsAdvisor]);

  const [fnr, setFnr] = useState("");
  const [name, setName] = useState("");
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [zip, setZip] = useState("");
  const [city, setCity] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState("+47");
  const [fetchingBrreg, setFetchingBrreg] = useState(false);
  const [brregSuccess, setBrregSuccess] = useState<string | null>(null);
  const [searchingCompanies, setSearchingCompanies] = useState(false);
  const [searchResults, setSearchResults] = useState<BrregCompany[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [fnrValidationStatus, setFnrValidationStatus] = useState<
    "idle" | "valid" | "invalid" | "exists"
  >("idle");

  const fetchPoststed = (zipCode: string) => {
    if (!zipCode || zipCode.length !== 4) return;
    const sted = findPoststed(zipCode);
    if (sted) setCity(sted);
  };

  const formatCompanyName = (name: string): string => {
    // Convert to title case for better readability
    return name
      .toLowerCase()
      .split(" ")
      .map((word) => {
        // Keep common abbreviations uppercase
        if (
          ["as", "asa", "ba", "nuf", "ans", "da", "ks", "sa"].includes(word)
        ) {
          return word.toUpperCase();
        }
        // Capitalize first letter of each word
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ");
  };

  const trimPhoneNumber = (phone: string): string => {
    // Remove all spaces from phone number
    return phone.replace(/\s+/g, "");
  };

  const searchCompaniesByName = async () => {
    if (!name || name.length < 2) {
      toast.error("Please enter at least 2 characters to search", {
        hideProgressBar: false,
        theme: "colored",
        autoClose: 3000,
      });
      return;
    }

    setSearchingCompanies(true);
    setSearchResults([]);

    try {
      const encodedName = encodeURIComponent(name);
      const response = await fetch(
        `https://data.brreg.no/enhetsregisteret/api/enheter?navn=${encodedName}&page=0&size=20`
      );

      if (!response.ok) {
        toast.error("Failed to search companies", {
          hideProgressBar: false,
          theme: "colored",
          autoClose: 3000,
        });
        return;
      }

      const data = await response.json();

      if (data._embedded?.enheter && data._embedded.enheter.length > 0) {
        setSearchResults(data._embedded.enheter);
        setShowSearchResults(true);
      } else {
        toast.info("No companies found with that name", {
          hideProgressBar: false,
          theme: "colored",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Error searching companies:", error);
      toast.error("Network error. Please try again.", {
        hideProgressBar: false,
        theme: "colored",
        autoClose: 3000,
      });
    } finally {
      setSearchingCompanies(false);
    }
  };

  const selectCompany = (company: BrregCompany) => {
    // Clear all fields first to avoid keeping old data
    setName("");
    setAddress1("");
    setAddress2("");
    setZip("");
    setCity("");
    setPhone("");

    // Populate all fields with selected company data
    setName(formatCompanyName(company.navn));
    setFnr(company.organisasjonsnummer);

    // Phone number if available (trimmed)
    if (company.mobil) {
      setPhone(normalizePhone(trimPhoneNumber(company.mobil)));
    }

    // Address data
    if (company.forretningsadresse) {
      const addr = company.forretningsadresse;
      if (addr.adresse && addr.adresse.length > 0) {
        setAddress1(addr.adresse[0]);
        if (addr.adresse.length > 1) {
          setAddress2(addr.adresse[1]);
        }
      }
      if (addr.postnummer) {
        setZip(addr.postnummer);
        if (addr.poststed) {
          setCity(addr.poststed);
        }
      }
    } else if (company.postadresse) {
      const addr = company.postadresse;
      if (addr.adresse && addr.adresse.length > 0) {
        setAddress1(addr.adresse[0]);
        if (addr.adresse.length > 1) {
          setAddress2(addr.adresse[1]);
        }
      }
      if (addr.postnummer) {
        setZip(addr.postnummer);
        if (addr.poststed) {
          setCity(addr.poststed);
        }
      }
    }

    setShowSearchResults(false);
    setSearchResults([]);
    setBrregSuccess(company.navn);

    // Clear errors for populated fields
    setErrors({
      type: "",
      name: "",
      fnr: "",
      address1: "",
      zip: "",
      city: "",
      email: "",
      phone: "",
      classification: "",
      advisorList: "",
    });

    // Validate fnr after setting it - check if contact exists
    setTimeout(() => {
      const fnrError = validateFnr(company.organisasjonsnummer);
      if (fnrError) {
        if (fnrError.includes("already exists")) {
          setFnrValidationStatus("exists");
        } else {
          setFnrValidationStatus("invalid");
        }
        setErrors((prev) => ({ ...prev, fnr: fnrError }));
      } else {
        setFnrValidationStatus("valid");
      }
    }, 0);

    setTimeout(() => setBrregSuccess(null), 5000);
  };

  const fetchFromBrreg = async () => {
    if (!fnr || fnr.length !== 9) {
      toast.error("Organization number must be 9 digits", {
        hideProgressBar: false,
        theme: "colored",
        autoClose: 3000,
      });
      return;
    }

    setFetchingBrreg(true);

    try {
      const response = await fetch(
        `https://data.brreg.no/enhetsregisteret/api/enheter/${fnr}`
      );

      if (!response.ok) {
        if (response.status === 404) {
          toast.error("Organization not found in Brønnøysund Register", {
            hideProgressBar: false,
            theme: "colored",
            autoClose: 3000,
          });
        } else {
          toast.error("Failed to fetch organization data", {
            hideProgressBar: false,
            theme: "colored",
            autoClose: 3000,
          });
        }
        return;
      }

      const data = await response.json();

      // Clear all fields first to avoid keeping old data
      setName("");
      setAddress1("");
      setAddress2("");
      setZip("");
      setCity("");
      setPhone("");

      // Populate fields with fetched data
      if (data.navn) setName(formatCompanyName(data.navn));

      // Phone number if available (trimmed)
      if (data.mobil) {
        setPhone(normalizePhone(trimPhoneNumber(data.mobil)));
      }

      // Address data
      if (data.forretningsadresse) {
        const addr = data.forretningsadresse;
        if (addr.adresse && addr.adresse.length > 0) {
          setAddress1(addr.adresse[0]);
          if (addr.adresse.length > 1) {
            setAddress2(addr.adresse[1]);
          }
        }
        if (addr.postnummer) {
          setZip(addr.postnummer);
          if (addr.poststed) {
            setCity(addr.poststed);
          }
        }
      } else if (data.postadresse) {
        // Fallback to postal address
        const addr = data.postadresse;
        if (addr.adresse && addr.adresse.length > 0) {
          setAddress1(addr.adresse[0]);
          if (addr.adresse.length > 1) {
            setAddress2(addr.adresse[1]);
          }
        }
        if (addr.postnummer) {
          setZip(addr.postnummer);
          if (addr.poststed) {
            setCity(addr.poststed);
          }
        }
      }

      setBrregSuccess(data.navn);
      // Clear success message after 5 seconds
      setTimeout(() => setBrregSuccess(null), 5000);
    } catch (error) {
      console.error("Error fetching from Brønnøysund:", error);
      toast.error("Network error. Please try again.", {
        hideProgressBar: false,
        theme: "colored",
        autoClose: 3000,
      });
    } finally {
      setFetchingBrreg(false);
    }
  };

  const addEmptyAdvisorToList = () => {
    const newList = [
      ...advisorList,
      {
        id: "",
        name: "",
        share: 0,
      },
    ];

    if (!useCustomShares) {
      // Distribute evenly
      const baseShare = Math.floor(100 / newList.length);
      const remainder = 100 - baseShare * newList.length;
      setAdvisorList(
        newList.map((advisor, index) => ({
          ...advisor,
          share: baseShare + (index < remainder ? 1 : 0),
        }))
      );
    } else {
      setAdvisorList(newList);
    }
  };

  const deleteAdvisorFromList = (index: number) => {
    setAdvisorList((prev) => {
      const updated = prev.filter((_, i) => i !== index);

      if (!useCustomShares && updated.length > 0) {
        // Distribute evenly
        const baseShare = Math.floor(100 / updated.length);
        const remainder = 100 - baseShare * updated.length;
        return updated.map((advisor, idx) => ({
          ...advisor,
          share: baseShare + (idx < remainder ? 1 : 0),
        }));
      }

      if (updated.length === 1) {
        return [{ ...updated[0], share: 100 }];
      }
      return updated;
    });
  };

  const resetForm = () => {
    setType("");
    setClassification("");
    setTaxCountry("NO");
    setLanguage("NO");
    setNationality("NO");
    setFnr("");
    setName("");
    setAddress1("");
    setAddress2("");
    setZip("");
    setCity("");
    setEmail("");
    setPhone("");
    setPhoneCountryCode("+47");
    setSearchResults([]);
    setShowSearchResults(false);
    setBrregSuccess(null);
    setFnrValidationStatus("idle");

    // Reset advisor list to just me
    if (meAsAdvisor) {
      setAdvisorList([
        {
          id: meAsAdvisor.contactId,
          name: meAsAdvisor.contactName,
          share: 100,
        },
      ]);
    } else {
      setAdvisorList([]);
    }

    // Reset custom shares
    setUseCustomShares(false);

    // Clear all errors
    setErrors({
      type: "",
      name: "",
      fnr: "",
      address1: "",
      zip: "",
      city: "",
      email: "",
      phone: "",
      classification: "",
      advisorList: "",
    });
  };

  const advisors = useMemo(() => {
    return (
      availableAdvisors?.map((x) => ({
        id: x.contactId,
        name: x.contactName,
      })) || []
    );
  }, [availableAdvisors]);

  const countryOptions = useMemo(() => {
    return (
      countries
        ?.filter((x) => x.type === "Country")
        .map((x) => ({
          code: x.code,
          name: x.name,
        })) || []
    );
  }, [countries]);

  // Validation functions
  const validateFnr = (value: string): string => {
    if (!value) return "Birth/Org Number is required";

    // Person validation (Norwegian birth number - 11 digits)
    if (type === "Person") {
      if (!/^\d+$/.test(value)) {
        return "Birth number must contain only digits";
      }
      if (value.length !== 11) {
        return "Birth number must be exactly 11 digits";
      }
    }
    // Company/Organization validation (Norwegian org number - 9 digits)
    else if (type === "Company") {
      if (!/^\d+$/.test(value)) {
        return "Organization number must contain only digits";
      }
      if (value.length !== 9) {
        return "Organization number must be exactly 9 digits";
      }
    }

    // Check if contact already exists
    if (doesContactExist(value)) {
      return "A contact with this ID already exists";
    }

    return "";
  };

  const validateEmail = (value: string): string => {
    if (!value) return "";
    const looksLikePhone = /^[+\d][\d\s\-().]{5,}$/.test(value);
    if (looksLikePhone)
      return "Looks like a phone number — please use the Phone field";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
      return "Invalid email address";
    return "";
  };

  const validatePhone = (value: string): string => {
    if (!value) return "";
    if (value.includes("@"))
      return "Looks like an email address — please use the Email field";
    if (/[a-zA-Z]/.test(value)) return "Phone number cannot contain letters";
    const digitsOnly = value.replace(/[\s\-().+]/g, "");
    if (!/^\d{7,15}$/.test(digitsOnly))
      return "Invalid phone number (7–15 digits expected)";
    return "";
  };

  const validateZip = (value: string): string => {
    if (!value) return "";
    if (taxCountry === "NO" && value.length !== 4) {
      return "Norwegian zip code must be 4 digits";
    }
    return "";
  };

  const validateAdvisorList = (): string => {
    if (advisorList.length === 0) {
      return "At least one advisor is required";
    }

    const hasEmptyAdvisor = advisorList.some((adv) => !adv.id || !adv.name);
    if (hasEmptyAdvisor) {
      return "All advisors must be selected";
    }

    if (useCustomShares) {
      if (advisorList.length < 2) {
        return "Custom shares requires at least 2 advisors";
      }
      if (advisorList.some((adv) => adv.share >= 100 || adv.share <= 0)) {
        return "Each advisor must have a share between 1% and 99%";
      }
    }

    const totalShare = advisorList.reduce((sum, adv) => sum + adv.share, 0);
    if (totalShare !== 100) {
      return `Total share must equal 100% (currently ${totalShare}%)`;
    }

    return "";
  };

  const validateField = (field: string, value: string | ContactAdvisor[]) => {
    let error = "";

    switch (field) {
      case "subType":
        error = !value ? "Subtype is required" : "";
        break;
      case "name":
        error = !value ? "Name is required" : "";
        break;
      case "fnr":
        error = validateFnr(value as string);
        break;
      case "address1":
        error = !value ? "Address is required" : "";
        break;
      case "zip":
        error = validateZip(value as string);
        break;
      case "city":
        error = !value ? "City is required" : "";
        break;
      case "email":
        error = validateEmail(value as string);
        break;
      case "phone":
        error = validatePhone(value as string);
        break;
      case "classification":
        error = !value ? "Classification is required" : "";
        break;
      case "advisorList":
        error = validateAdvisorList();
        break;
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return error;
  };

  const validateAllFields = (): boolean => {
    const newErrors = {
      type: !type ? "Type is required" : "",
      name: !name ? "Name is required" : "",
      fnr: validateFnr(fnr),
      address1: !address1 ? "Address is required" : "",
      zip: validateZip(zip),
      city: !city ? "City is required" : "",
      email: validateEmail(email),
      phone: validatePhone(phone),
      classification: !classification ? "Classification is required" : "",
      advisorList: validateAdvisorList(),
    };

    setErrors(newErrors);

    // Return true if no errors
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const { importContacts, submitting } = useImportContacts();

  const handleCreateContact = async () => {
    // Validate all fields before submission
    const isValid = validateAllFields();

    if (!isValid) {
      return;
    }

    // Additional check for existing contact
    if (doesContactExist(fnr)) {
      toast.error(
        `A contact with ID F${fnr} already exists. Please use a different ID.`,
        {
          hideProgressBar: false,
          theme: "colored",
          autoClose: 5000,
        }
      );
      setErrors((prev) => ({ ...prev, fnr: "Contact already exists" }));
      return;
    }

    // Check for duplicate email / phone among existing prospects
    if (email.trim()) {
      const normalizedEmail = email.trim().toLowerCase();
      const prospectEmailMatch = prospectData.find(
        (p) => (p.primaryAddress?.email || "").toLowerCase() === normalizedEmail
      );
      if (prospectEmailMatch) {
        toast.error(
          `Email "${email.trim()}" is already registered to prospect ${
            prospectEmailMatch.name
          }.`,
          { hideProgressBar: false, theme: "colored", autoClose: 5000 }
        );
        setErrors((prev) => ({ ...prev, email: "Already used by a prospect" }));
        return;
      }
    }

    if (phone.trim()) {
      const normalizedPhone = normalizePhone(phone.trim());
      const prospectPhoneMatch = prospectData.find(
        (p) =>
          normalizePhone(p.primaryAddress?.phone1 || "") === normalizedPhone
      );
      if (prospectPhoneMatch) {
        toast.error(
          `Phone "${phone.trim()}" is already registered to prospect ${
            prospectPhoneMatch.name
          }.`,
          { hideProgressBar: false, theme: "colored", autoClose: 5000 }
        );
        setErrors((prev) => ({ ...prev, phone: "Already used by a prospect" }));
        return;
      }
    }

    const contactData = {
      contactId: `F${fnr}`,
      externalId: fnr,
      name: name,
      type: "1",
      juridical: type,
      status: "A",
      taxCountry: taxCountry,
      classification: classification,
      identity: "KNOWN",
      nationality: nationality,
      language: language,
      representatives: advisorList
        .map(
          (adv) =>
            `${adv.id}###Type-MainAdvisor${
              useCustomShares ? `#AdvisorSplit-${adv.share.toFixed(0)}` : ""
            }`
        )
        .join(","),
      address1: address1,
      address2: address2,
      zipCode: zip,
      city: city,
      phone1: phone.trim()
        ? `${phoneCountryCode}${phone.replace(/\s/g, "")}`
        : "",
      email: email,
    };

    const res = await importContacts(contactData);

    if (res && res.data?.importContacts[0]?.importStatus === "OK") {
      toast.success("Contact created successfully", {
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        theme: "colored",
      });
      await refetch();
      await refetchContacts();
      onClose && onClose();
    }
  };

  return (
    <div className="flex flex-col gap-3 pt-1.5">
      {/* Basic Information Section */}
      <Box>
        <Typography
          variant="caption"
          fontWeight="600"
          mb={1}
          display="block"
          color="text.secondary"
        >
          Basic Information
        </Typography>
        <Box display="flex" flexDirection="column" gap={1.5}>
          <TextField
            select
            label="Type"
            size="small"
            fullWidth
            value={type}
            onChange={(e) => {
              setType(e.target.value);
              setErrors((prev) => ({ ...prev, type: "", fnr: "" }));
            }}
            onBlur={() => validateField("type", type)}
            error={!!errors.type}
            helperText={errors.type}
            required
          >
            {SUBTYPES.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1 relative">
              <div className="flex gap-2 items-center">
                <TextField
                  label="Name"
                  size="small"
                  fullWidth
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setErrors((prev) => ({ ...prev, name: "" }));
                    setShowSearchResults(false);
                  }}
                  onBlur={() => validateField("name", name)}
                  error={!!errors.name}
                  helperText={errors.name}
                  placeholder="Name"
                  required
                />
                {type === "Company" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={searchCompaniesByName}
                    disabled={searchingCompanies || name.length < 2}
                    icon={
                      searchingCompanies ? (
                        <Loader2 className="size-3 animate-spin" />
                      ) : (
                        <Search className="size-3" />
                      )
                    }
                    className="whitespace-nowrap self-start mt-1"
                    title="Search for company by name"
                  >
                    {searchingCompanies ? "Searching..." : "Search"}
                  </Button>
                )}
              </div>
              {showSearchResults && searchResults.length > 0 && (
                <Paper
                  className="absolute top-full left-0 right-0 -mt-4 max-h-60 overflow-y-auto z-50 shadow-lg"
                  sx={{ border: 1, borderColor: "divider" }}
                >
                  <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-3 py-2 flex items-center justify-between z-10">
                    <Typography variant="caption" color="text.secondary">
                      {searchResults.length} companies found
                    </Typography>
                    <Button
                      variant="icon"
                      size="sm"
                      onClick={() => {
                        setShowSearchResults(false);
                        setSearchResults([]);
                      }}
                      title="Close search results"
                    >
                      <X className="size-4" />
                    </Button>
                  </div>
                  <div className="py-1">
                    {searchResults.map((company, index) => (
                      <div
                        key={index}
                        className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer border-b border-gray-200 dark:border-gray-700 last:border-b-0"
                        onClick={() => selectCompany(company)}
                      >
                        <div className="font-medium text-sm">
                          {company.navn}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Org.nr: {company.organisasjonsnummer}
                        </div>
                        {company.forretningsadresse?.poststed && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {company.forretningsadresse.poststed}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </Paper>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <div className="flex gap-2 items-start">
                <div className="flex-1 relative">
                  <TextField
                    label={
                      type === "Person"
                        ? "National Identification Number"
                        : "Organization number"
                    }
                    size="small"
                    fullWidth
                    value={fnr}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFnr(value);
                      setBrregSuccess(null);

                      // Live validation
                      if (!value) {
                        setFnrValidationStatus("idle");
                        setErrors((prev) => ({ ...prev, fnr: "" }));
                        return;
                      }

                      const error = validateFnr(value);
                      if (error) {
                        if (error.includes("already exists")) {
                          setFnrValidationStatus("exists");
                        } else {
                          setFnrValidationStatus("invalid");
                        }
                        setErrors((prev) => ({ ...prev, fnr: error }));
                      } else {
                        setFnrValidationStatus("valid");
                        setErrors((prev) => ({ ...prev, fnr: "" }));
                      }
                    }}
                    onBlur={() => validateField("fnr", fnr)}
                    error={!!errors.fnr}
                    helperText={
                      errors.fnr ||
                      (type === "Person"
                        ? "11 digits"
                        : type === "Company"
                        ? "9 digits"
                        : "")
                    }
                    placeholder={
                      type === "Person" ? "11 digits" : "Organization number"
                    }
                    required
                  />
                  {fnrValidationStatus !== "idle" && fnr && (
                    <div className="absolute right-3 top-2">
                      {fnrValidationStatus === "valid" && (
                        <CheckCircle2 className="size-5 text-green-600 dark:text-green-400" />
                      )}
                      {(fnrValidationStatus === "invalid" ||
                        fnrValidationStatus === "exists") && (
                        <X className="size-5 text-red-600 dark:text-red-400" />
                      )}
                    </div>
                  )}
                </div>
                {type === "Company" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchFromBrreg}
                    disabled={fetchingBrreg || fnr.length !== 9}
                    icon={
                      fetchingBrreg ? (
                        <Loader2 className="size-3 animate-spin" />
                      ) : (
                        <Building2 className="size-3" />
                      )
                    }
                    className="mt-1 whitespace-nowrap self-start"
                    title="Fetch company data from Brønnøysund Register"
                  >
                    {fetchingBrreg ? "Fetching..." : "Fetch"}
                  </Button>
                )}
              </div>
              {brregSuccess && (
                <div className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400 animate-in fade-in slide-in-from-top-1 duration-300 px-1">
                  <CheckCircle2 className="size-3.5" />
                  <span className="font-medium">
                    Data loaded: {brregSuccess}
                  </span>
                </div>
              )}
            </div>
          </div>
          <TextField
            select
            label="Classification"
            size="small"
            fullWidth
            required
            value={classification}
            onChange={(e) => {
              setClassification(e.target.value);
              setErrors((prev) => ({ ...prev, classification: "" }));
            }}
            error={!!errors.classification}
            helperText={errors.classification}
          >
            <MenuItem value="NPROF">Non-Professional</MenuItem>
            <MenuItem value="PROF">Professional</MenuItem>
            <MenuItem value="NCLAS">Non-Classified</MenuItem>
          </TextField>
          <div className="grid grid-cols-2 gap-2">
            <TextField
              label="Email"
              size="small"
              fullWidth
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setErrors((prev) => ({ ...prev, email: "" }));
              }}
              onBlur={() => validateField("email", email)}
              error={!!errors.email}
              helperText={errors.email}
              placeholder="email@example.com"
            />
            <TextField
              label="Phone"
              size="small"
              fullWidth
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                setErrors((prev) => ({ ...prev, phone: "" }));
              }}
              onBlur={() => validateField("phone", phone)}
              error={!!errors.phone}
              helperText={errors.phone}
              placeholder="123 45 678"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 0 }}>
                    <Select
                      value={phoneCountryCode}
                      onChange={(e) =>
                        setPhoneCountryCode(e.target.value as string)
                      }
                      variant="standard"
                      disableUnderline
                      renderValue={(val) => {
                        const entry = PHONE_COUNTRY_CODES.find(
                          (p) => p.code === val
                        );
                        return (
                          <span className="inline-flex items-center gap-1">
                            {entry && (
                              <CircleFlag
                                countryCode={entry.iso}
                                height={14}
                                width={14}
                              />
                            )}
                            <span style={{ fontSize: 12 }}>
                              {val as string}
                            </span>
                          </span>
                        );
                      }}
                      sx={{
                        fontSize: "12px",
                        minWidth: 64,
                        mr: 0.5,
                        "& .MuiSelect-select": {
                          pr: "20px !important",
                          py: 0,
                          display: "flex",
                          alignItems: "center",
                        },
                        "& .MuiSvgIcon-root": { fontSize: "14px" },
                      }}
                    >
                      {PHONE_COUNTRY_CODES.map(({ code, iso }) => (
                        <MenuItem
                          key={code}
                          value={code}
                          sx={{
                            fontSize: "12px",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <CircleFlag
                            countryCode={iso}
                            height={16}
                            width={16}
                          />
                          {code}
                        </MenuItem>
                      ))}
                    </Select>
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                  </InputAdornment>
                ),
              }}
            />
          </div>
        </Box>
      </Box>

      {/* Address Section */}
      <Box>
        <Typography
          variant="caption"
          fontWeight="600"
          mb={1}
          display="block"
          color="text.secondary"
        >
          Address
        </Typography>
        <Box display="flex" flexDirection="column" gap={1.5}>
          <TextField
            label="Address Line 1"
            size="small"
            fullWidth
            value={address1}
            onChange={(e) => {
              setAddress1(e.target.value);
              setErrors((prev) => ({ ...prev, address1: "" }));
            }}
            onBlur={() => validateField("address1", address1)}
            error={!!errors.address1}
            helperText={errors.address1}
            placeholder="Street address"
            required
          />
          <TextField
            label="Address Line 2"
            size="small"
            fullWidth
            value={address2}
            onChange={(e) => setAddress2(e.target.value)}
            placeholder="Apartment, suite, etc. (optional)"
          />
          <div className="grid grid-cols-2 gap-2">
            <TextField
              label="Zip code"
              size="small"
              fullWidth
              value={zip}
              onChange={(e) => {
                setZip(e.target.value);
                setErrors((prev) => ({ ...prev, zip: "" }));
                if (e.target.value.length === 4) {
                  fetchPoststed(e.target.value);
                }
              }}
              onBlur={() => {
                fetchPoststed(zip);
                validateField("zip", zip);
              }}
              error={!!errors.zip}
              helperText={errors.zip}
              placeholder="Zip code"
            />
            <TextField
              label="City"
              size="small"
              fullWidth
              value={city}
              onChange={(e) => {
                setCity(e.target.value);
                setErrors((prev) => ({ ...prev, city: "" }));
              }}
              onBlur={() => validateField("city", city)}
              error={!!errors.city}
              helperText={errors.city}
              placeholder="City"
              required
            />
          </div>
        </Box>
      </Box>

      {/* Regional Settings Section */}
      <Box>
        <Typography
          variant="caption"
          fontWeight="600"
          mb={1}
          display="block"
          color="text.secondary"
        >
          Regional Settings
        </Typography>
        <Box display="flex" flexDirection="column" gap={1.5}>
          <div className="grid grid-cols-3 gap-2">
            <Autocomplete
              options={countryOptions}
              getOptionLabel={(option) => option.name || ""}
              value={countryOptions.find((c) => c.code === taxCountry) || null}
              onChange={(_, newValue) => {
                setTaxCountry(newValue?.code || "NO");
              }}
              size="small"
              loading={countriesLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tax Country"
                  size="small"
                  fullWidth
                />
              )}
            />
            <TextField
              select
              label="Language"
              size="small"
              fullWidth
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
            >
              {LANGUAGES.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>
            <Autocomplete
              options={countryOptions}
              getOptionLabel={(option) => option.name || ""}
              value={countryOptions.find((c) => c.code === nationality) || null}
              onChange={(_, newValue) => {
                setNationality(newValue?.code || "NO");
              }}
              size="small"
              loading={countriesLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Nationality"
                  size="small"
                  fullWidth
                />
              )}
            />
          </div>
        </Box>
      </Box>

      {/* Advisors Section */}
      <Box>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Typography
            variant="caption"
            fontWeight="600"
            display="block"
            color="text.secondary"
          >
            Advisors
            <Typography component="span" color="error" ml={0.5}>
              *
            </Typography>
          </Typography>
          <div className="flex items-center justify-end">
            <FormControlLabel
              control={
                <Checkbox
                  checked={useCustomShares}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setUseCustomShares(checked);
                    if (!checked && advisorList.length > 0) {
                      // Distribute evenly when unchecking
                      const baseShare = Math.floor(100 / advisorList.length);
                      const remainder = 100 - baseShare * advisorList.length;
                      setAdvisorList((prev) =>
                        prev.map((advisor, index) => ({
                          ...advisor,
                          share: baseShare + (index < remainder ? 1 : 0),
                        }))
                      );
                    }
                  }}
                  size="small"
                  sx={{ p: 0.25 }}
                />
              }
              label={
                <Typography variant="caption" color="text.secondary">
                  Custom shares
                </Typography>
              }
              sx={{ m: 0 }}
            />
          </div>
        </Box>
        <Box
          display="grid"
          gridTemplateColumns={useCustomShares ? "1fr 120px 50px" : "1fr 50px"}
          gap={0.75}
          mb={0.75}
          alignItems="center"
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="caption" color="text.secondary">
              Advisor
            </Typography>
          </Box>
          {useCustomShares && (
            <Typography variant="caption" color="text.secondary">
              Share (%)
            </Typography>
          )}
          <Box />
        </Box>

        {advisorList.length === 0 && (
          <Box
            p={2}
            sx={{
              border: "2px dashed",
              borderColor: "hsl(var(--border))",
              borderRadius: 1,
              textAlign: "center",
              backgroundColor: "hsl(var(--muted) / 0.3)",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No advisors selected
            </Typography>
          </Box>
        )}

        {advisorList.map((advisor, i) => (
          <Box
            key={i}
            display="grid"
            gridTemplateColumns={
              useCustomShares ? "1fr 120px 50px" : "1fr 50px"
            }
            gap={0.75}
            mb={0.75}
            alignItems="center"
          >
            <Autocomplete
              options={
                advisors.filter(
                  (x) => !advisorList.map((x) => x.id).includes(x.id)
                ) || []
              }
              getOptionLabel={(option) => option.name}
              value={advisor}
              onChange={(_, newValue) => {
                if (newValue) {
                  const newList = [...advisorList];
                  newList[i] = {
                    ...advisor,
                    id: newValue.id,
                    name: newValue.name,
                  };
                  setAdvisorList(newList);
                  setErrors((prev) => ({ ...prev, advisorList: "" }));
                }
              }}
              size="small"
              disabled={loadingAdvisors}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select advisor"
                  size="small"
                  fullWidth
                />
              )}
            />
            {useCustomShares && (
              <TextField
                select
                size="small"
                value={advisor.share}
                onChange={(e) => {
                  const newList = [...advisorList];
                  newList[i] = {
                    ...advisor,
                    share: Number(e.target.value),
                  };
                  setAdvisorList(newList);
                  setErrors((prev) => ({ ...prev, advisorList: "" }));
                }}
                SelectProps={{
                  MenuProps: {
                    style: { maxHeight: 300 },
                  },
                }}
              >
                {advisorSplits.map((split) => (
                  <MenuItem key={split} value={split}>
                    {split}%
                  </MenuItem>
                ))}
              </TextField>
            )}
            <Button
              variant="icon"
              size="sm"
              onClick={() => deleteAdvisorFromList(i)}
              color="error"
            >
              <Trash2 size={18} />
            </Button>
          </Box>
        ))}

        <Box display="flex" gap={1} mt={1}>
          <Button
            size="sm"
            variant="outline"
            icon={<Plus size={16} />}
            onClick={() => {
              addEmptyAdvisorToList();
              setErrors((prev) => ({ ...prev, advisorList: "" }));
            }}
            disabled={loadingAdvisors}
          >
            Add advisor
          </Button>
        </Box>
        {errors.advisorList && (
          <Typography color="error" variant="caption" display="block" mt={1}>
            {errors.advisorList}
          </Typography>
        )}
      </Box>

      <DialogActions className="px-0 pt-3">
        <Button onClick={onClose} variant="ghost" size="sm">
          Cancel
        </Button>
        <Button onClick={resetForm} variant="outline" size="sm">
          Reset
        </Button>
        <Button
          disabled={submitting}
          loading={submitting}
          onClick={handleCreateContact}
          variant="default"
          size="sm"
        >
          Submit
        </Button>
      </DialogActions>
    </div>
  );
};
